import { supabase } from '@/lib/supabase/client';
import type {
  AtRiskFlagListFilters,
  AtRiskFlagStatus,
  AtRiskSignalType,
  LocationAtRiskFlag,
  LocationAtRiskFlagInsert,
  LocationAtRiskFlagUpdate,
} from '@/types/customer-ops';

function nullableText(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  return trimmed || null;
}

type AtRiskFilterQuery<T> = {
  eq(column: string, value: string): T;
  in(column: string, values: readonly string[]): T;
};

function applyAtRiskFilters<T extends AtRiskFilterQuery<T>>(query: T, filters?: AtRiskFlagListFilters): T {
  let next = query;
  if (!filters) return next;

  if (filters.status) {
    const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
    next = next.in('status', statuses);
  }
  if (filters.signalType) {
    const signalTypes = Array.isArray(filters.signalType) ? filters.signalType : [filters.signalType];
    next = next.in('signal_type', signalTypes);
  }
  if (filters.source) {
    const sources = Array.isArray(filters.source) ? filters.source : [filters.source];
    next = next.in('source', sources);
  }
  if (filters.locationId) {
    next = next.eq('location_id', filters.locationId);
  }
  if (filters.owner) {
    next = next.eq('owner', filters.owner);
  }
  return next;
}

export const locationAtRiskFlagsService = {
  async list(filters?: AtRiskFlagListFilters): Promise<LocationAtRiskFlag[]> {
    try {
      let query = supabase
        .from('location_at_risk_flags')
        .select('*')
        .order('updated_at', { ascending: false });
      query = applyAtRiskFilters(query, filters);

      const { data, error } = await query;
      if (error) {
        console.error('Error fetching at-risk flags:', error);
        return [];
      }
      return (data as LocationAtRiskFlag[]) || [];
    } catch (error) {
      console.error('Error fetching at-risk flags:', error);
      return [];
    }
  },

  async getById(id: string): Promise<LocationAtRiskFlag | null> {
    try {
      const { data, error } = await supabase.from('location_at_risk_flags').select('*').eq('id', id).single();
      if (error) {
        if (error.code === 'PGRST116') return null;
        console.error('Error fetching at-risk flag:', error);
        return null;
      }
      return (data as LocationAtRiskFlag) || null;
    } catch (error) {
      console.error('Error fetching at-risk flag:', error);
      return null;
    }
  },

  async create(data: LocationAtRiskFlagInsert): Promise<LocationAtRiskFlag> {
    if (data.source !== 'manual') {
      throw new Error('Only manual at-risk flags can be created from the UI.');
    }

    const insertData = {
      location_id: data.location_id,
      signal_type: data.signal_type,
      source: 'manual' as const,
      status: data.status ?? 'open',
      owner: nullableText(data.owner),
      next_action: nullableText(data.next_action),
      notes: nullableText(data.notes),
    };

    const { data: created, error } = await supabase
      .from('location_at_risk_flags')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      console.error('Error creating at-risk flag:', error);
      throw new Error(`Failed to create at-risk flag: ${error.message}`);
    }
    return created as LocationAtRiskFlag;
  },

  async update(id: string, data: LocationAtRiskFlagUpdate): Promise<LocationAtRiskFlag> {
    const updateData: Record<string, unknown> = {};
    if (data.status !== undefined) updateData.status = data.status;
    if (data.owner !== undefined) updateData.owner = nullableText(data.owner);
    if (data.next_action !== undefined) updateData.next_action = nullableText(data.next_action);
    if (data.notes !== undefined) updateData.notes = nullableText(data.notes);
    if (data.resolved_at !== undefined) updateData.resolved_at = data.resolved_at;

    const { data: updated, error } = await supabase
      .from('location_at_risk_flags')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating at-risk flag:', error);
      throw new Error(`Failed to update at-risk flag: ${error.message}`);
    }
    return updated as LocationAtRiskFlag;
  },

  async setStatus(id: string, status: AtRiskFlagStatus): Promise<LocationAtRiskFlag> {
    const resolved_at = status === 'resolved' ? new Date().toISOString() : null;
    return this.update(id, { status, resolved_at });
  },

  async upsertComputedFlag(
    locationId: string,
    signalType: AtRiskSignalType,
    options?: { notes?: string | null; status?: AtRiskFlagStatus }
  ): Promise<LocationAtRiskFlag> {
    const { data: existingRows, error: fetchError } = await supabase
      .from('location_at_risk_flags')
      .select('*')
      .eq('location_id', locationId)
      .eq('signal_type', signalType)
      .eq('source', 'computed')
      .in('status', ['open', 'monitoring']);

    if (fetchError) {
      throw new Error(`Failed to look up computed at-risk flag: ${fetchError.message}`);
    }

    const existing = (existingRows as LocationAtRiskFlag[] | null)?.[0];
    if (existing) {
      const nextNotes = options?.notes !== undefined ? nullableText(options.notes) : existing.notes;
      const nextStatus = options?.status ?? existing.status;
      if (nextNotes !== existing.notes || nextStatus !== existing.status) {
        return this.update(existing.id, { notes: nextNotes, status: nextStatus });
      }
      return existing;
    }

    const { data: resolvedRows } = await supabase
      .from('location_at_risk_flags')
      .select('*')
      .eq('location_id', locationId)
      .eq('signal_type', signalType)
      .eq('source', 'computed')
      .eq('status', 'resolved')
      .order('updated_at', { ascending: false })
      .limit(1);

    const resolved = (resolvedRows as LocationAtRiskFlag[] | null)?.[0];
    if (resolved) {
      return this.update(resolved.id, {
        status: options?.status ?? 'open',
        resolved_at: null,
        notes: options?.notes !== undefined ? nullableText(options.notes) : resolved.notes,
      });
    }

    const { data: created, error } = await supabase
      .from('location_at_risk_flags')
      .insert({
        location_id: locationId,
        signal_type: signalType,
        source: 'computed',
        status: options?.status ?? 'open',
        notes: nullableText(options?.notes),
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create computed at-risk flag: ${error.message}`);
    }
    return created as LocationAtRiskFlag;
  },

  async resolveComputedFlag(id: string): Promise<LocationAtRiskFlag> {
    return this.setStatus(id, 'resolved');
  },

  async countOpen(): Promise<number> {
    const rows = await this.list({ status: ['open', 'monitoring'] });
    return rows.length;
  },
};
